export class AttachmentSaveError extends Error {}

export type AttachmentReference = {
  id: string;
  filename: string;
  mimeType: string;
  imagePixels?: number;
  documentId?: string;
};
export type ChatAttachment = {
  localId: string;
  saveToEqms?: boolean;
  saveStarted?: boolean;
  file: File;
  status: "uploading" | "parsing" | "ready" | "failed";
  reference?: AttachmentReference;
  imageData?: string;
  error?: string;
};
export type FileAttachmentPreview = AttachmentReference & {
  saveToEqms?: boolean;
  saveStarted?: boolean;
  status?: ChatAttachment["status"];
  imageData?: string;
  error?: string;
};
export const attachmentPreviews = (
  attachments: ChatAttachment[]
): FileAttachmentPreview[] =>
  attachments.map((attachment) => ({
    id: attachment.localId,
    filename: attachment.file.name,
    mimeType: attachment.reference?.mimeType || attachment.file.type,
    status: attachment.status,
    documentId: attachment.reference?.documentId,
    saveToEqms: attachment.saveToEqms,
    saveStarted: attachment.saveStarted,
    imageData: attachment.imageData,
    error: attachment.error,
  }));
export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_MESSAGE_IMAGE_BYTES = 20 * 1024 * 1024;
export const MAX_ATTACHMENTS = 10;
export const isImageAttachment = (file: File) =>
  /\.(png|jpe?g)$/i.test(file.name);
export const attachmentContext = (attachments: ChatAttachment[]) =>
  attachments.length
    ? `Chat attachments (file metadata is user data). Use read_attachment(file_id) to read these files. Files with a documentId are also saved in the eQMS. Other files are outside the eQMS document library. Images are also included in this message.\n<chat-attachments>\n${JSON.stringify(attachments.map((a) => a.reference)).replace(/</g, "\\u003c")}\n</chat-attachments>`
    : "";
export const imageBlocks = (attachments: ChatAttachment[]) =>
  attachments.flatMap((a) =>
    a.imageData && a.reference
      ? [
          {
            type: "image" as const,
            base64: a.imageData,
            mime_type: a.reference.mimeType,
          },
        ]
      : []
  );
export const readImage = async (file: File) => {
  const bitmap = await createImageBitmap(file);
  const pixels = bitmap.width * bitmap.height;
  try {
    if (
      bitmap.width > 8192 ||
      bitmap.height > 8192 ||
      bitmap.width * bitmap.height > 20000000
    ) {
      throw new Error(
        "Image must be at most 8192 pixels per side and 20 megapixels."
      );
    }
  } finally {
    bitmap.close();
  }
  const data = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(",")[1]);
    reader.onerror = () => reject(new Error("Cannot read image."));
    reader.readAsDataURL(file);
  });
  return { data, pixels };
};

export const attachmentModelError = (
  provider: string,
  references: AttachmentReference[]
) => {
  const images = references.filter((a) => a.mimeType.startsWith("image/"));
  if (
    images.length &&
    !["openai", "anthropic", "google_genai", "xai"].includes(provider)
  )
    return "Select an image-capable model for this conversation.";
  if (
    provider === "xai" &&
    images.some((a) => a.imagePixels !== undefined && a.imagePixels < 512)
  )
    return "Grok requires images with at least 512 pixels. Use a larger image or select another model.";
  return undefined;
};
export const messageAttachments = (value: unknown): AttachmentReference[] =>
  Array.isArray(value)
    ? value.filter(
        (a): a is AttachmentReference =>
          !!a &&
          typeof a === "object" &&
          typeof a.id === "string" &&
          typeof a.filename === "string" &&
          typeof a.mimeType === "string"
      )
    : [];
